/**
 * Created by rathompson on 2/20/24
 * File: AddTenantPage.js
 * Project: cloud-mgmt-modules ©
 * ©
 */
import RCTree from '../../../common/components/page/rcTree/RCTree'
import AddTenant from './AddTenant'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import GlobalContext from '../../../common/context/GlobalContext'
import { ROOT_NODE_ID, TENANT_ADD, TENANT_DELETE, TENANT_EDIT } from '../../constants/TenancyConstants'
import { useIsMounted, useModal } from '@panwds/react-ui'
import { safeSet } from '../../../common/utils/Utils'
import TenancyService from '../../services/TenancyService'
import { TSG_NONE } from '../../../common/constants/GlobalConstants'
import { LoadTreeDataAsync } from '../../../common/actions/GlobalActions'
import { actions, history } from '@sparky/framework'
import _ from 'lodash'
import DeleteTSGModal from './Modal/DeleteTSGModal'

const { switchTsg } = actions

const AddTenantPage = (props) => {

  const deleteTSGModal = useModal(DeleteTSGModal)
  const isMounted = useIsMounted()
  const { treeReducer = {}, dispatch } = useContext(GlobalContext)
  const { selectedTreeNode, sgKeyBy } = treeReducer
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [actionType, setActionType] = useState(TENANT_EDIT)
  const [popoverTenant, setPopoverTenant] = useState(selectedTreeNode)
  const [readOnly, setReadOnly] = useState(true)
  const [formValues, setFormValues] = useState({})

  const handleAdd = useCallback(async ({ display_name, vertical, support_contact }) => {
    safeSet(setLoading, true, isMounted.current)
    const tsg_id = popoverTenant?.id !== ROOT_NODE_ID ? popoverTenant?.id : TSG_NONE.id
    const formValue = { display_name, support_contact, vertical }
    if (popoverTenant?.id !== ROOT_NODE_ID) {
      formValue.parent_id = popoverTenant?.id
    }
    const { data: new_tsg } = await TenancyService.listTenant.post(formValue, tsg_id)
    await LoadTreeDataAsync(dispatch)
    history.push('/settings/tenants')
    await switchTsg(new_tsg?.id, { forceReload: true })
  }, [dispatch, isMounted, popoverTenant?.id])

  const handleEdit = useCallback(async ({ display_name, vertical, support_contact }) => {
    const formValue = { display_name, support_contact, vertical }
    if (popoverTenant?.id !== ROOT_NODE_ID && popoverTenant?.parent_id !== ROOT_NODE_ID) {
      formValue.parent_id = popoverTenant?.parent_id
    }
    const { data } = await TenancyService.listTenant.put(popoverTenant?.id, formValue)
    await LoadTreeDataAsync(dispatch)
    safeSet(setPopoverTenant, data, isMounted.current)
    safeSet(setActionType, TENANT_EDIT, isMounted.current)
  }, [dispatch, isMounted, popoverTenant])

  const onSubmit = useCallback(async (values) => {
    try {
      safeSet(setLoading, true, isMounted.current)
      if (actionType === TENANT_ADD) {
        await handleAdd(values)
      }
      else if (actionType === TENANT_EDIT) {
        await handleEdit(values)
      }
      safeSet(setReadOnly, true, isMounted.current)
    }
    catch (error) {
      safeSet(setError, error.message, isMounted.current)
    }
    finally {
      safeSet(setLoading, false, isMounted.current)
    }
  }, [actionType, handleAdd, handleEdit, isMounted])

  const openDeleteTSGModal = useCallback(() => {
    const tenant = _.get(props, 'selectedTreeNode')
    const instances = _.get(props, `instancesKeyBy.${selectedTreeNode?.id}`, [])
    const modalProps = {
      tenant,
      instances
    }
    deleteTSGModal.openModal(modalProps)
  }, [deleteTSGModal, props, selectedTreeNode?.id])

  const onContextClick = useCallback((actionType, tenant) => {
    setPopoverTenant(tenant)
    if (actionType === TENANT_DELETE) {
      openDeleteTSGModal()
    }
    safeSet(setActionType, actionType, isMounted.current)
  }, [isMounted, openDeleteTSGModal])

  const editFormValues = useMemo(() => {
    const parent_contact = sgKeyBy[selectedTreeNode?.parent_id]?.support_contact || ''
    const custom_contact = selectedTreeNode?.support_contact === parent_contact ? '' : selectedTreeNode?.support_contact
    const initial = selectedTreeNode?.isRoot || (Boolean(custom_contact) && custom_contact !== parent_contact) ? 'custom' : 'parent'
    const support_contact = {
      parent: parent_contact,
      custom: custom_contact,
      initial
    }
    return {
      display_name: selectedTreeNode?.display_name,
      vertical: { value: selectedTreeNode?.vertical },
      support_contact
    }
  }, [selectedTreeNode, sgKeyBy])

  const addFormValues = useMemo(() => {
    const parent_contact = selectedTreeNode?.support_contact || ''
    const support_contact = {
      parent: parent_contact,
      custom: '',
      initial: 'parent'
    }
    return { support_contact }
  }, [selectedTreeNode?.support_contact])

  useEffect(() => {
    switch (actionType) {
      case TENANT_EDIT:
        safeSet(setFormValues, editFormValues, isMounted.current)
        break
      case TENANT_ADD:
      default:
        safeSet(setFormValues, addFormValues, isMounted.current)
        safeSet(setReadOnly, false, isMounted.current)
    }
  }, [actionType, addFormValues, editFormValues, isMounted])

  const onCancel = useCallback(() => {
    safeSet(setReadOnly, true, isMounted.current)
    safeSet(setActionType, TENANT_EDIT, isMounted.current)
  }, [isMounted])

  return (
    <div className="cm-flex cm-flex-row cm-flex-1 cm-absolute cm-inset-0 cm-overflow-hidden">
      <div className="common-module-side-bar dark:cm-bg-gray-950">
        <RCTree
          {...{ ...props, onContextClick }}
          expandedKeys={[selectedTreeNode?.parent_id]}
          showContextButton
          basic
        />
      </div>
      <AddTenant
        readOnly={readOnly}
        setReadOnly={setReadOnly}
        isLoading={loading}
        error={error}
        onCancel={onCancel}
        actionType={actionType}
        formValues={formValues}
        onSubmit={onSubmit}
        selectedTreeNode={popoverTenant}
      />
    </div>
  )
}

export default AddTenantPage
